'use client';

import React, { useState } from 'react';
import { Slide, MediaSource } from '@/shared/types';
import { ArchiveIcon, CaretDownIcon } from '@phosphor-icons/react';
import { Button } from '@tranquility-ai/theme-components/button';
import SimplifiedSources from '../cells/simplifiedSources';
import { motion } from 'framer-motion';
import { useTimePilotT } from '@/i18n/useTimePilotT';

const EMPTY_SOURCES: MediaSource[] = [];
// Interfaces and types
interface SourcesPaneProps {
  slide: Slide;
  onToggle?: (isExpanded: boolean) => void;
}

// Component definition
const SourcesPane: React.FC<SourcesPaneProps> = ({ slide, onToggle }) => {
  // Initialize state
  const [showSources, setShowSources] = useState(false);
  const t = useTimePilotT();

  return (
    <div className={`flex flex-col gap-2 slide-boxes hover:text-white text-white/65 transition-colors duration-300 w-full p-1 ${showSources ? 'pb-3' : ''} px-[10px]`}>
      <Button
        style="text"
        color="light"
        background="inverse"
        className="flex flex-row items-center gap-2 w-full active:bg-transparent justify-between hover:bg-transparent h-auto"
        aria-expanded={showSources}
        aria-label={t('sources.toggle')}
        onClick={() => {
          const newState = !showSources;
          setShowSources(newState);
          onToggle?.(newState);
        }}
      >
        <div className='flex flex-row items-center gap-2 text-lg'>
          <ArchiveIcon weight="duotone" className='w-6 h-6' />
          {t('sources.heading')}
        </div>
        <CaretDownIcon
          weight="bold"
          className={`w-5 h-5 mr-4 transform transition-transform duration-300 ${showSources ? '-rotate-180' : ''}`}
        />
      </Button>
        {showSources && (
          // Opacity only. A height animation reports a short offsetHeight, and the
          // slide locks the column to that until the next window resize.
          <motion.div
            data-testid='sources-container-pane'
            className='flex min-h-0 flex-col gap-2 pl-2 pr-0 max-h-[250px]'
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.2 }}
          >
            <SimplifiedSources
              value={slide.sources ?? EMPTY_SOURCES}
              slide={Number(slide.id)}
              isInPresentationView={true}
              editingEnabled={false}
            />
          </motion.div>
        )}
    </div>
  );
}

export default SourcesPane;